import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const collectionsApi = axios.create({
  baseURL: `${API_URL}/api/v1/collections`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request/Response logging in development
collectionsApi.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 [CollectionsService] ${config.method.toUpperCase()} ${config.url}`, config.params || config.data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

collectionsApi.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📥 [CollectionsService] ${response.config.method.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`💥 [CollectionsService] Error:`, error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

export const collectionsService = {
  /**
   * Get all collections for a specific product
   * @param {string} productId - Product ID
   * @param {Object} params - Query params (type, status, etc.)
   * @returns {Promise<Object>} Collections data
   */
  async getProductCollections(productId, params = {}) {
    console.log('📤 [CollectionsService] Fetching collections for product:', productId);
    try {
      const queryParams = new URLSearchParams({
        productId,
        status: 'active',
        ...params
      });
      const response = await collectionsApi.get(`/product/${productId}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionsService] Error fetching product collections:', error);
      throw error;
    }
  },

  /**
   * Get collection by ID with all its children
   * @param {string} collectionId - Collection ID
   * @returns {Promise<Object>} Collection data with children
   */
  async getCollectionWithChildren(collectionId) {
    console.log('📤 [CollectionsService] Fetching collection with children:', collectionId);
    try {
      const response = await collectionsApi.get(`/${collectionId}/with-children`);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionsService] Error fetching collection with children:', error);
      throw error;
    }
  },

  /**
   * Get all parent collections for a product
   * @param {string} productId - Product ID
   * @returns {Promise<Array>} Parent collections
   */
  async getParentCollections(productId) {
    console.log('📤 [CollectionsService] Fetching parent collections for product:', productId);
    try {
      const response = await collectionsApi.get(`/parent-collections/${productId}`);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionsService] Error fetching parent collections:', error);
      throw error;
    }
  },

  /**
   * Get collection breadcrumb path
   * @param {string} collectionId - Collection ID
   * @returns {Promise<Array>} Breadcrumb path
   */
  async getCollectionBreadcrumb(collectionId) {
    console.log('📤 [CollectionsService] Fetching breadcrumb for collection:', collectionId);
    try {
      const response = await collectionsApi.get(`/${collectionId}/breadcrumb`);
      return response.data;
    } catch (error) {
      console.error('💥 [CollectionsService] Error fetching breadcrumb:', error);
      throw error;
    }
  },

  // Utility methods
  formatPrice(price) {
    if (price === undefined || price === null) return 'Price on request';
    return `₦${parseFloat(price).toLocaleString()}`;
  },

  getCollectionTypeIcon(type) {
    const icons = {
      country: '🌍',
      theme: '🎨',
      category: '📁',
      occasion: '🎉',
      other: '📦'
    };
    return icons[type] || '📦';
  },

  getStockStatus(stockQuantity) {
    if (stockQuantity <= 0) return { text: 'Out of Stock', color: 'text-red-600', badge: 'bg-red-100 text-red-800' };
    if (stockQuantity < 10) return { text: `Low Stock (${stockQuantity})`, color: 'text-yellow-600', badge: 'bg-yellow-100 text-yellow-800' };
    return { text: 'In Stock', color: 'text-green-600', badge: 'bg-green-100 text-green-800' };
  }
};

export default collectionsService;